/**
 * AiSeedModal — DEV-1354 v1. Admin-only tool to AI-seed a draft chronicle.
 * Server-side, web-grounded. Shows the per-source reliability read before the
 * admin opens the draft to review. English-only (system-admin UI convention).
 */

import { useState } from 'react';
import { seedChronicle, type SeedChronicleResponse } from '../../api/aiSeed';

interface Props {
  onClose: () => void;
  onOpenDraft: (chronicleGuid: string) => void;
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const card: React.CSSProperties = {
  width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto', borderRadius: 12,
  border: '1px solid var(--border-default)', backgroundColor: 'var(--bg-primary)',
  backgroundImage: 'linear-gradient(var(--bg-elevated), var(--bg-elevated))', boxShadow: 'var(--shadow-lg)',
};
const field: React.CSSProperties = {
  width: '100%', padding: '8px 11px', borderRadius: 8, border: '1px solid var(--border-default)',
  background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 14,
};
const label: React.CSSProperties = { display: 'block', fontSize: 13, color: 'var(--text-tertiary)', marginBlockEnd: 6 };

function reliabilityColor(r: string | null): string {
  switch ((r || '').toLowerCase()) {
    case 'high': return 'var(--color-success)';
    case 'medium': return 'var(--color-warning)';
    case 'low': return 'var(--color-error)';
    default: return 'var(--text-muted)';
  }
}

export default function AiSeedModal({ onClose, onOpenDraft }: Props) {
  const [topic, setTopic] = useState('');
  const [chronicleType, setChronicleType] = useState('topic_tracker');
  const [intent, setIntent] = useState('general');
  const [urlsText, setUrlsText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SeedChronicleResponse | null>(null);

  const handleGenerate = async () => {
    if (!topic.trim()) { setError('Enter a topic.'); return; }
    setGenerating(true);
    setError(null);
    try {
      const sourceUrls = urlsText.split('\n').map((u) => u.trim()).filter(Boolean);
      const res = await seedChronicle({ topic: topic.trim(), chronicleType, intent, sourceUrls });
      setResult(res);
    } catch (e: any) {
      setError(e?.message || 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    // No backdrop-click dismissal — a stray click must not discard a long form
    // or a finished generation (Skip 2026-06-17). Close only via ×, Cancel, Done.
    <div style={overlay}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBlockEnd: '1px solid var(--border-subtle)' }}>
          <h2 style={{ margin: 0, fontSize: 16, color: 'var(--text-primary)' }}>Seed with AI</h2>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 0, color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* ── Result view ── */}
        {result ? (
          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
              Draft created. <strong>Review before publishing.</strong>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
              AI confidence: <strong style={{ color: 'var(--text-secondary)' }}>{result.aiConfidence || '—'}</strong>
              {result.intent ? <> · intent: <strong style={{ color: 'var(--text-secondary)' }}>{result.intent}</strong></> : null}
            </div>
            {result.notes && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 10 }}>
                <div style={{ fontWeight: 700, marginBlockEnd: 4 }}>Verify:</div>{result.notes}
              </div>
            )}
            {result.sourceReliability.length > 0 && (
              <div>
                <div style={{ ...label, marginBlockEnd: 8 }}>Source reliability</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {result.sourceReliability.map((s, i) => (
                    <div key={i} style={{ fontSize: 12.5, border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '8px 10px' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBlockEnd: 2 }}>
                        <span style={{ fontWeight: 700, color: reliabilityColor(s.reliability), textTransform: 'uppercase', fontSize: 10.5 }}>{s.reliability || 'unknown'}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: 10.5, textTransform: 'uppercase' }}>{s.type}</span>
                        <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label || s.url}</span>
                      </div>
                      {s.reliabilityRationale && <div style={{ color: 'var(--text-tertiary)' }}>{s.reliabilityRationale}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBlockStart: 6 }}>
              <button type="button" onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}>Done</button>
              <button type="button" onClick={() => onOpenDraft(result.chronicleGuid)} style={{ padding: '8px 16px', borderRadius: 8, border: 0, background: 'var(--accent-primary)', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Open draft</button>
            </div>
          </div>
        ) : (
          /* ── Form view ── */
          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={label}>Topic</label>
              <input style={field} autoFocus value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Cold Fusion" disabled={generating} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={label}>Type</label>
                <select style={field} value={chronicleType} onChange={(e) => setChronicleType(e.target.value)} disabled={generating}>
                  <option value="topic_tracker">Topic Tracker</option>
                  <option value="project">Project Chronicle</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={label}>Intent</label>
                <select style={field} value={intent} onChange={(e) => setIntent(e.target.value)} disabled={generating}>
                  <option value="general">General topic</option>
                  <option value="problem">Problem</option>
                  <option value="solution">Solution</option>
                </select>
              </div>
            </div>
            <div>
              <label style={label}>Pre-vetted source URLs (one per line, optional)</label>
              <textarea style={{ ...field, resize: 'vertical', minHeight: 70 }} value={urlsText} onChange={(e) => setUrlsText(e.target.value)} placeholder={'https://...\nhttps://...'} disabled={generating} />
            </div>
            {error && <div style={{ color: 'var(--color-error)', fontSize: 13 }}>{error}</div>}
            {generating && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Researching the web and drafting… this can take a few minutes — leave this open.</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBlockStart: 6 }}>
              <button type="button" onClick={onClose} disabled={generating} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button type="button" onClick={handleGenerate} disabled={generating} style={{ padding: '8px 16px', borderRadius: 8, border: 0, background: 'var(--accent-primary)', color: '#fff', cursor: generating ? 'wait' : 'pointer', fontWeight: 600 }}>{generating ? 'Generating…' : 'Generate draft'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
